const runtimeEnv = (typeof window !== "undefined" && window.__ENV) ? window.__ENV : {};
const PUBLIC_KEY_PEM = runtimeEnv.PUBLIC_KEY_PEM || process.env.REACT_APP_PUBLIC_KEY_PEM || "";

async function getPublicKeyPem() {
    return normalizePemResponse(PUBLIC_KEY_PEM);
}

function normalizePemResponse(rawValue) {
    if (!rawValue) {
        return "";
    }
    const text = String(rawValue).trim();

    try {
        const parsed = JSON.parse(text);
        const candidate = parsed && parsed.publicKeyPem ? String(parsed.publicKeyPem).trim() : "";
        if (candidate.indexOf("-----BEGIN PUBLIC KEY-----") !== -1) {
            return candidate.replace(/\\n/g, "\n");
        }
    } catch (error) {
        // Si no es JSON, se intenta como PEM plano.
    }

    if (text.indexOf("-----BEGIN PUBLIC KEY-----") !== -1) {
        return text.replace(/\\n/g, "\n");
    }

    return "";
}

function pemToArrayBuffer(pem) {
    const cleaned = pem
        .replace(/-----BEGIN PUBLIC KEY-----/g, "")
        .replace(/-----END PUBLIC KEY-----/g, "")
        .replace(/\s+/g, "");
    const binary = atob(cleaned);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

function arrayBufferToBase64Url(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function stringToBase64Url(text) {
    const bytes = new TextEncoder().encode(text);
    return arrayBufferToBase64Url(bytes.buffer);
}

async function importPublicKey(pem) {
    const keyData = pemToArrayBuffer(pem);
    return window.crypto.subtle.importKey(
        "spki",
        keyData,
        {
            name: "RSA-OAEP",
            hash: "SHA-256",
        },
        false,
        ["encrypt"]
    );
}

async function encryptAesGcm(plaintextBytes) {
    const key = await window.crypto.subtle.generateKey(
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"]
    );
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await window.crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        plaintextBytes
    );
    const rawKey = await window.crypto.subtle.exportKey("raw", key);
    return { ciphertext, iv, rawKey };
}

async function encryptWithPublicKey(publicKey, data) {
    return window.crypto.subtle.encrypt(
        { name: "RSA-OAEP" },
        publicKey,
        data
    );
}

export async function codificarValorSeguro(value) {
    if (typeof window === "undefined" || !window.crypto || !window.crypto.subtle) {
        throw new Error("Cifrado v1 no disponible: WebCrypto no soportado");
    }

    const pem = await getPublicKeyPem();
    if (!pem) {
        throw new Error("Cifrado v1 no disponible: llave publica no configurada");
    }

    let publicKey;
    try {
        publicKey = await importPublicKey(pem);
    } catch (error) {
        throw new Error(`Cifrado v1 fallo al importar llave publica: ${error && error.message ? error.message : "error desconocido"}`);
    }

    let plaintextBytes;
    try {
        const jsonValue = JSON.stringify(value);
        plaintextBytes = new TextEncoder().encode(jsonValue);
    } catch (error) {
        throw new Error(`Cifrado v1 fallo al serializar payload: ${error && error.message ? error.message : "error desconocido"}`);
    }

    let aesPack;
    try {
        aesPack = await encryptAesGcm(plaintextBytes);
    } catch (error) {
        throw new Error(`Cifrado v1 fallo en AES-GCM: ${error && error.message ? error.message : "error desconocido"}`);
    }

    let encryptedKey;
    try {
        encryptedKey = await encryptWithPublicKey(publicKey, aesPack.rawKey);
    } catch (error) {
        throw new Error(`Cifrado v1 fallo al cifrar clave AES con RSA: ${error && error.message ? error.message : "error desconocido"}`);
    }

    try {
        const payload = {
            v: 1,
            alg: "RSA-OAEP-256+A256GCM",
            ek: arrayBufferToBase64Url(encryptedKey),
            iv: arrayBufferToBase64Url(aesPack.iv.buffer),
            ct: arrayBufferToBase64Url(aesPack.ciphertext),
        };
        return `v1.${stringToBase64Url(JSON.stringify(payload))}`;
    } catch (error) {
        throw new Error(`Cifrado v1 fallo al construir sobre final: ${error && error.message ? error.message : "error desconocido"}`);
    }
}
